import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Card, CardContent, Button, Chip, Alert, AlertTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Tooltip, Avatar,
  List, ListItem, ListItemText, ListItemAvatar, Divider,
  Tabs, Tab, Grid
} from '@mui/material';
import {
  Computer, CheckCircle, Error, Warning, Refresh,
  Build, Speed, BugReport,
  RestartAlt, Settings, Visibility,
  Hub, Sync, History, LocalShipping, Assignment,
  Article
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import {
  useWorkstationMonitoringStatus,
  useWorkstationMetrics,
  useRemediationEvents,
  useHardwareEscalations,
  useSendRemediationCommand,
  useResolveEscalation,
  useTaskTrackingStatistics,
  useTaskTrackingEvents,
  useWorkstationLogs,
  useTransportLabels
} from '../../hooks/useWorkstations';

const WorkstationMonitoringPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedWorkstation, setSelectedWorkstation] = useState(null);
  const [commandDialogOpen, setCommandDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [commandForm, setCommandForm] = useState({
    action: 'restart_service',
    service: '',
    device_type: ''
  });

  const refetchOpts = { refetchInterval: autoRefresh ? 10000 : false };

  const { data: monitoringStatus, isLoading: statusLoading, refetch: refetchStatus } = useWorkstationMonitoringStatus(refetchOpts);
  const { data: metricsData } = useWorkstationMetrics(selectedWorkstation);
  const { data: eventsData } = useRemediationEvents({ limit: 50 });
  const { data: escalationsData } = useHardwareEscalations({ resolved: false });

  const { data: taskStatsData } = useTaskTrackingStatistics(refetchOpts);
  const { data: taskEventsData } = useTaskTrackingEvents({ limit: 100 }, refetchOpts);
  const { data: logsData } = useWorkstationLogs({ limit: 100 }, { refetchInterval: autoRefresh ? 15000 : false });
  const { data: labelsData } = useTransportLabels({ refetchInterval: autoRefresh ? 30000 : false });

  const sendCommand = useSendRemediationCommand();
  const resolveEscalationMutation = useResolveEscalation();

  const workstations = monitoringStatus?.workstations || [];
  const onlineCount = monitoringStatus?.online_count ?? workstations.filter(w => w.online).length;
  const offlineCount = monitoringStatus?.offline_count ?? workstations.filter(w => !w.online).length;
  const events = eventsData?.events || [];
  const escalations = escalationsData?.escalations || [];

  const taskStats = taskStatsData || {};
  const taskEvents = taskEventsData?.events || taskEventsData || [];
  const logEntries = logsData?.logs || logsData?.entries || (Array.isArray(logsData) ? logsData : []);
  const labels = labelsData?.labels || labelsData?.transport_labels || (Array.isArray(labelsData) ? labelsData : []);

  const handleSendCommand = useCallback(async () => {
    if (!selectedWorkstation) return;
    try {
      await sendCommand.mutateAsync({
        station_code: selectedWorkstation,
        action: commandForm.action,
        service: commandForm.service || undefined,
        device_type: commandForm.device_type || undefined
      });
      enqueueSnackbar(t('common.workstation.commandSuccess', 'Command sent successfully'), { variant: 'success' });
      setCommandDialogOpen(false);
    } catch (error) {
      enqueueSnackbar(`${t('common.workstation.commandFailed', 'Failed to send command')}: ${error.message}`, { variant: 'error' });
    }
  }, [selectedWorkstation, commandForm, sendCommand, enqueueSnackbar, t]);

  const handleResolveEscalation = useCallback(async () => {
    if (!selectedEscalation) return;
    try {
      await resolveEscalationMutation.mutateAsync({
        escalationId: selectedEscalation.id,
        resolutionNotes
      });
      enqueueSnackbar(t('common.workstation.escalationResolved', 'Escalation resolved'), { variant: 'success' });
      setResolveDialogOpen(false);
      setSelectedEscalation(null);
      setResolutionNotes('');
    } catch (error) {
      enqueueSnackbar(`${t('common.workstation.resolveFailed', 'Failed to resolve escalation')}: ${error.message}`, { variant: 'error' });
    }
  }, [selectedEscalation, resolutionNotes, resolveEscalationMutation, enqueueSnackbar, t]);

  const getStatusIcon = (online) => {
    if (online) return <CheckCircle sx={{ color: '#4CAF50' }} />;
    return <Error sx={{ color: '#F44336' }} />;
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getOpStatusColor = (status) => {
    if (!status) return 'default';
    const s = String(status).toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'shipped') return 'success';
    if (s === 'in_progress' || s === 'started' || s === 'active') return 'warning';
    if (s === 'failed' || s === 'error' || s === 'cancelled') return 'error';
    return 'info';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (typeof timestamp === 'string') {
      const d = new Date(timestamp);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    if (typeof timestamp === 'number') {
      const d = timestamp > 1e12 ? new Date(timestamp) : new Date(timestamp * 1000);
      return d.toLocaleString();
    }
    return 'N/A';
  };

  const formatSecondsAgo = (seconds) => {
    if (seconds === null || seconds === undefined) return 'Never';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const handleRefreshAll = () => {
    refetchStatus();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <Computer />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('common.workstation.title', 'AI Workstation Monitoring')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('common.workstation.subtitle', 'Autonomous monitoring and auto-remediation system')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={autoRefresh ? t('common.disableAutoRefresh', 'Disable Auto Refresh') : t('common.enableAutoRefresh', 'Enable Auto Refresh')}>
            <IconButton onClick={() => setAutoRefresh(!autoRefresh)} color={autoRefresh ? 'primary' : 'default'}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.refreshData', 'Refresh data')}>
            <IconButton onClick={handleRefreshAll} color="primary">
              <Sync />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {escalations.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>{t('common.workstation.escalationsTitle', 'Hardware Escalations Requiring Attention')}</AlertTitle>
          {t('common.workstation.escalationsCount', '{{count}} hardware issue(s) require human intervention', { count: escalations.length })}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h3" color="primary.main">{workstations.length}</Typography>
            <Typography variant="body2" color="text.secondary">{t('common.workstation.totalWorkstations', 'Registered Workstations')}</Typography>
          </CardContent></Card>
        </Box>
        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h3" color="success.main">{onlineCount}</Typography>
            <Typography variant="body2" color="text.secondary">{t('common.online', 'Online')}</Typography>
          </CardContent></Card>
        </Box>
        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h3" color="error.main">{offlineCount}</Typography>
            <Typography variant="body2" color="text.secondary">{t('common.offline', 'Offline')}</Typography>
          </CardContent></Card>
        </Box>
        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h3" color="warning.main">{escalations.length}</Typography>
            <Typography variant="body2" color="text.secondary">{t('common.workstation.pendingEscalations', 'Pending Escalations')}</Typography>
          </CardContent></Card>
        </Box>
        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h3" color="info.main">{taskStats.total_events || 0}</Typography>
            <Typography variant="body2" color="text.secondary">{t('common.workstation.taskEvents', 'Task Events')}</Typography>
          </CardContent></Card>
        </Box>
        <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h3" color="secondary.main">{labels.length}</Typography>
            <Typography variant="body2" color="text.secondary">{t('common.workstation.shippingLabels', 'Shipping Labels')}</Typography>
          </CardContent></Card>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={t('common.workstation.tabStatus', 'Status')} icon={<Computer />} iconPosition="start" />
          <Tab label={t('common.workstation.tabOperations', 'Operations')} icon={<Assignment />} iconPosition="start" />
          <Tab label={t('common.workstation.tabMesh', 'Mesh Network')} icon={<Hub />} iconPosition="start" />
          <Tab label={t('common.workstation.tabLabels', 'Labels')} icon={<LocalShipping />} iconPosition="start" />
          <Tab label={t('common.workstation.tabLogs', 'Logs')} icon={<Article />} iconPosition="start" />
          <Tab label={t('common.workstation.tabEscalations', 'Escalations')} icon={<BugReport />} iconPosition="start" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Computer sx={{ color: '#2196F3' }} />
                {t('common.workstation.status', 'Registered Workstations')}
              </Typography>
              <SmartDataGrid
                columns={[
                  { id: 'online', label: t('common.status', 'Status'), render: (val, row) => getStatusIcon(row.online) },
                  { id: 'hostname', label: t('common.hostname', 'Hostname'), render: (val, row) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{row.hostname || '-'}</Typography> },
                  { id: 'station_code', label: t('common.stationCode', 'Station Code'), render: (val, row) => <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.station_code}</Typography> },
                  { id: 'type', label: t('common.type', 'Type'), render: (val, row) => <Chip label={row.workstation_type || '-'} size="small" /> },
                  { id: 'local_ip', label: t('common.localIp', 'Local IP'), render: (val, row) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.local_ip || '-'}</Typography> },
                  { id: 'tailscale_ip', label: t('common.tailscaleIp', 'Tailscale IP'), render: (val, row) => <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.tailscale_ip || '-'}</Typography> },
                  { id: 'health', label: t('common.health', 'Health'), render: (val, row) => <Chip label={row.health_status || (row.online ? 'healthy' : 'offline')} size="small" color={row.online ? 'success' : 'default'} /> },
                  { id: 'lastSeen', label: t('common.lastSeen', 'Last Seen'), render: (val, row) => <Typography variant="body2" color="text.secondary">{formatSecondsAgo(row.last_seen_seconds_ago)}</Typography> },
                  {
                    id: 'actions',
                    label: t('common.actions', 'Actions'),
                    render: (val, row) => (
                      <Box>
                        <Tooltip title={t('common.viewMetrics', 'View metrics')}><IconButton size="small" onClick={() => setSelectedWorkstation(row.station_code)}><Visibility /></IconButton></Tooltip>
                        <Tooltip title={t('common.sendCommand', 'Send command')}><IconButton size="small" onClick={() => { setSelectedWorkstation(row.station_code); setCommandDialogOpen(true); }}><Settings /></IconButton></Tooltip>
                        <Tooltip title={t('common.restartServices', 'Restart services')}><IconButton size="small" color="warning" onClick={() => { setSelectedWorkstation(row.station_code); setCommandForm({ action: 'restart_service', service: 'all', device_type: '' }); setCommandDialogOpen(true); }}><RestartAlt /></IconButton></Tooltip>
                      </Box>
                    )
                  }
                ]}
                data={workstations.map((ws, i) => ({ ...ws, id: ws.station_code || i }))}
                loading={statusLoading}
                exportable={true}
                quickFilters={[
                  { key: 'online', label: t('common.online', 'Online'), color: 'success' },
                  { key: 'offline', label: t('common.offline', 'Offline'), color: 'error' }
                ]}
              />
            </CardContent>
          </Card>

          {metricsData && selectedWorkstation && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Speed sx={{ color: '#4CAF50' }} />
                  {t('common.workstation.metricsFor', 'Metrics for')} {selectedWorkstation}
                </Typography>
                <Grid container spacing={2}>
                  {metricsData.health_status && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">{t('common.health', 'Health')}</Typography>
                      <Chip label={metricsData.health_status} color={getHealthColor(metricsData.health_status)} size="small" />
                    </Grid>
                  )}
                  {metricsData.metrics && typeof metricsData.metrics === 'object' && Object.entries(metricsData.metrics).map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      <Typography variant="body2" color="text.secondary">{key.replace(/_/g, ' ')}</Typography>
                      <Typography variant="body1">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build sx={{ color: '#4CAF50' }} />
                    {t('common.workstation.recentEvents', 'Recent Remediation Events')}
                  </Typography>
                  {events.length === 0 ? (
                    <Typography color="text.secondary">{t('common.workstation.noEvents', 'No remediation events recorded yet.')}</Typography>
                  ) : (
                    <List dense>
                      {events.slice(0, 10).map((event, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: event.success ? 'success.main' : 'error.main', width: 32, height: 32 }}>
                              {event.success ? <CheckCircle fontSize="small" /> : <Error fontSize="small" />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${event.station_code}: ${event.action}`}
                            secondary={<>{event.message}<br /><Typography variant="caption" color="text.secondary">{formatTimestamp(event.timestamp)} | By: {event.triggered_by}</Typography></>}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Box>

            {taskStats.by_type && Object.keys(taskStats.by_type).length > 0 && (
              <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment sx={{ color: '#FF9800' }} />
                      {t('common.workstation.opsSummary', 'Task Tracking Summary')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">{t('common.workstation.totalEvents', 'Total Events')}</Typography>
                        <Typography variant="h5" color="primary.main">{taskStats.total_events || 0}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">{t('common.workstation.totalWorkstations', 'Workstations')}</Typography>
                        <Typography variant="h5" color="info.main">{taskStats.total_workstations || workstations.length}</Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>{t('common.workstation.completedByType', 'Events by Type')}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Object.entries(taskStats.by_type).map(([opType, count]) => (
                          <Chip key={opType} label={`${opType}: ${count}`} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment sx={{ color: '#FF9800' }} />
            {t('common.workstation.operationTracking', 'Operation Tracking')}
          </Typography>

          {taskStats.by_type && Object.keys(taskStats.by_type).length > 0 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {Object.entries(taskStats.by_type).map(([opType, count]) => (
                <Grid item xs={12} sm={6} md={2} key={opType}>
                  <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <Typography variant="h4" color="info.main">{count}</Typography>
                    <Typography variant="caption" color="text.secondary">{opType}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History sx={{ color: '#2196F3' }} />
                {t('common.workstation.taskEvents', 'Task Tracking Events')}
                <Chip label={Array.isArray(taskEvents) ? taskEvents.length : 0} size="small" color="info" sx={{ ml: 1 }} />
              </Typography>
              {(!Array.isArray(taskEvents) || taskEvents.length === 0) ? (
                <Alert severity="info">{t('common.workstation.noHistory', 'No task tracking events yet.')}</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.workstation.stationCode', 'Station')}</TableCell>
                        <TableCell>{t('common.workstation.eventType', 'Event Type')}</TableCell>
                        <TableCell>{t('common.workstation.entityId', 'Entity ID')}</TableCell>
                        <TableCell>{t('common.status', 'Status')}</TableCell>
                        <TableCell>{t('common.workstation.time', 'Time')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taskEvents.map((ev, idx) => (
                        <TableRow key={ev.id || idx}>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ev.station_code || ev.workstation_id || '-'}</Typography></TableCell>
                          <TableCell><Chip label={ev.event_type || ev.type || '-'} size="small" /></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ev.entity_id || ev.order_id || '-'}</Typography></TableCell>
                          <TableCell><Chip label={ev.status || '-'} size="small" color={getOpStatusColor(ev.status)} /></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{formatTimestamp(ev.event_timestamp || ev.timestamp || ev.created_at)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Hub sx={{ color: '#00C853' }} />
            {t('common.workstation.meshNetwork', 'Mesh Network')}
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main">{workstations.length}</Typography>
                <Typography variant="body2" color="text.secondary">{t('common.workstation.totalNodes', 'Total Nodes')}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">{onlineCount}</Typography>
                <Typography variant="body2" color="text.secondary">{t('common.workstation.connectedPeers', 'Connected')}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">{offlineCount}</Typography>
                <Typography variant="body2" color="text.secondary">{t('common.workstation.disconnectedPeers', 'Disconnected')}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">{labels.length}</Typography>
                <Typography variant="body2" color="text.secondary">{t('common.workstation.syncedLabels', 'Synced Labels')}</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('common.workstation.peers', 'Mesh Peers (Workstations)')}</Typography>
              {workstations.length === 0 ? (
                <Alert severity="info">{t('common.workstation.noPeers', 'No workstations registered yet.')}</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.status', 'Status')}</TableCell>
                        <TableCell>{t('common.hostname', 'Hostname')}</TableCell>
                        <TableCell>{t('common.stationCode', 'Station Code')}</TableCell>
                        <TableCell>{t('common.type', 'Type')}</TableCell>
                        <TableCell>{t('common.macAddress', 'MAC Address')}</TableCell>
                        <TableCell>{t('common.lastSeen', 'Last Seen')}</TableCell>
                        <TableCell>{t('common.workstation.registeredAt', 'Registered')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {workstations.map((ws, idx) => (
                        <TableRow key={ws.station_code || idx}>
                          <TableCell>{getStatusIcon(ws.online)}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{ws.hostname || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ws.station_code || '-'}</Typography></TableCell>
                          <TableCell><Chip label={ws.workstation_type || 'unknown'} size="small" /></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ws.mac_address || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{formatSecondsAgo(ws.last_seen_seconds_ago)}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{formatTimestamp(ws.registered_at)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping sx={{ color: '#E91E63' }} />
            {t('common.workstation.shippingLabels', 'Shipping Labels')}
          </Typography>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('common.workstation.cachedLabels', 'Transport Labels')}</Typography>
              {labels.length === 0 ? (
                <Alert severity="info">{t('common.workstation.noLabels', 'No transport labels found.')}</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.workstation.orderId', 'Order ID')}</TableCell>
                        <TableCell>{t('common.workstation.carrier', 'Carrier')}</TableCell>
                        <TableCell>{t('common.workstation.tracking', 'Tracking')}</TableCell>
                        <TableCell>{t('common.workstation.labelStatus', 'Status')}</TableCell>
                        <TableCell>{t('common.workstation.createdAt', 'Created')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {labels.map((label, idx) => (
                        <TableRow key={label.id || idx}>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{label.shipment_unit_id || label.order_id || '-'}</Typography></TableCell>
                          <TableCell>{label.carrier_id || label.carrier_name || label.carrier || '-'}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{label.tracking_number || '-'}</Typography></TableCell>
                          <TableCell><Chip label={label.status || 'unknown'} size="small" color={label.status === 'printed' ? 'success' : label.status === 'generated' ? 'info' : 'default'} /></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{formatTimestamp(label.created_at)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Article sx={{ color: '#607D8B' }} />
            {t('common.workstation.agentLogs', 'Workstation Logs')}
          </Typography>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('common.workstation.recentLogs', 'Recent Log Entries')}</Typography>
              {(!Array.isArray(logEntries) || logEntries.length === 0) ? (
                <Alert severity="info">{t('common.workstation.noLogs', 'No log entries available.')}</Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.workstation.stationCode', 'Station')}</TableCell>
                        <TableCell>{t('common.workstation.time', 'Time')}</TableCell>
                        <TableCell>{t('common.workstation.level', 'Level')}</TableCell>
                        <TableCell>{t('common.workstation.message', 'Message')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logEntries.slice(-100).map((entry, idx) => {
                        const level = entry.level || entry.severity || 'info';
                        const levelLower = String(level).toLowerCase();
                        const levelColor = levelLower === 'error' ? 'error' : levelLower === 'warn' || levelLower === 'warning' ? 'warning' : 'default';
                        return (
                          <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{entry.station_code || '-'}</Typography></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="caption" color="text.secondary">{formatTimestamp(entry.timestamp || entry.time || entry.created_at)}</Typography>
                            </TableCell>
                            <TableCell><Chip label={level} size="small" color={levelColor} /></TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                {entry.message || entry.msg || JSON.stringify(entry)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 5 && (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReport sx={{ color: '#F44336' }} />
                    {t('common.workstation.hardwareEscalations', 'Hardware Escalations')}
                  </Typography>
                  {escalations.length === 0 ? (
                    <Alert severity="success">{t('common.workstation.noEscalations', 'No hardware escalations pending. All issues are being handled automatically.')}</Alert>
                  ) : (
                    <List>
                      {escalations.map((escalation, index) => (
                        <React.Fragment key={escalation.id}>
                          <ListItem
                            secondaryAction={
                              <Button size="small" variant="outlined" onClick={() => { setSelectedEscalation(escalation); setResolveDialogOpen(true); }}>
                                {t('common.resolve', 'Resolve')}
                              </Button>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: `${getSeverityColor(escalation.severity)}.main` }}><Warning /></Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={`${escalation.station_code}: ${escalation.issue_type}`}
                              secondary={<>{escalation.description}<br /><Typography variant="caption" color="text.secondary">Attempts: {escalation.attempts} | {formatTimestamp(escalation.timestamp)}</Typography></>}
                            />
                          </ListItem>
                          {index < escalations.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 500px', minWidth: '400px' }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build sx={{ color: '#4CAF50' }} />
                    {t('common.workstation.recentEvents', 'Recent Remediation Events')}
                  </Typography>
                  {events.length === 0 ? (
                    <Typography color="text.secondary">{t('common.workstation.noEvents', 'No remediation events recorded yet.')}</Typography>
                  ) : (
                    <List dense>
                      {events.slice(0, 10).map((event, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: event.success ? 'success.main' : 'error.main', width: 32, height: 32 }}>
                              {event.success ? <CheckCircle fontSize="small" /> : <Error fontSize="small" />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${event.station_code}: ${event.action}`}
                            secondary={<>{event.message}<br /><Typography variant="caption" color="text.secondary">{formatTimestamp(event.timestamp)} | By: {event.triggered_by}</Typography></>}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={commandDialogOpen} onClose={() => setCommandDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.workstation.sendCommand', 'Send Remediation Command')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Target: {selectedWorkstation}</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('common.action', 'Action')}</InputLabel>
            <Select value={commandForm.action} onChange={(e) => setCommandForm(prev => ({ ...prev, action: e.target.value }))} label="Action">
              <MenuItem value="restart_service">{t('common.workstation.actions.restartService', 'Restart Service')}</MenuItem>
              <MenuItem value="reload_config">{t('common.workstation.actions.reloadConfig', 'Reload Configuration')}</MenuItem>
              <MenuItem value="force_reconnect">{t('common.workstation.actions.forceReconnect', 'Force Reconnect Device')}</MenuItem>
              <MenuItem value="clear_circuit_breaker">{t('common.workstation.actions.clearCircuitBreaker', 'Clear Circuit Breaker')}</MenuItem>
              <MenuItem value="publish_metrics">{t('common.workstation.actions.publishMetrics', 'Force Publish Metrics')}</MenuItem>
              <MenuItem value="get_status">{t('common.workstation.actions.getStatus', 'Get Detailed Status')}</MenuItem>
            </Select>
          </FormControl>
          {commandForm.action === 'restart_service' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('common.service', 'Service')}</InputLabel>
              <Select value={commandForm.service} onChange={(e) => setCommandForm(prev => ({ ...prev, service: e.target.value }))} label={t('common.service', 'Service')}>
                <MenuItem value="all">{t('common.workstation.services.all', 'All Services')}</MenuItem>
                <MenuItem value="upload">{t('common.workstation.services.upload', 'Upload Service')}</MenuItem>
                <MenuItem value="scanner">{t('common.workstation.services.scanner', 'Scanner Service')}</MenuItem>
                <MenuItem value="printer">{t('common.workstation.services.printer', 'Printer Service')}</MenuItem>
                <MenuItem value="camera">{t('common.workstation.services.camera', 'Camera Service')}</MenuItem>
                <MenuItem value="object_detection">{t('common.workstation.services.objectDetection', 'Object Detection')}</MenuItem>
              </Select>
            </FormControl>
          )}
          {commandForm.action === 'force_reconnect' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('common.deviceType', 'Device Type')}</InputLabel>
              <Select value={commandForm.device_type} onChange={(e) => setCommandForm(prev => ({ ...prev, device_type: e.target.value }))} label={t('common.deviceType', 'Device Type')}>
                <MenuItem value="scanner">{t('common.devices.scanner', 'Scanner')}</MenuItem>
                <MenuItem value="printer">{t('common.devices.printer', 'Printer')}</MenuItem>
                <MenuItem value="camera">{t('common.devices.camera', 'Camera')}</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommandDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleSendCommand} variant="contained" disabled={sendCommand.isPending}>
            {sendCommand.isPending ? t('common.sending', 'Sending...') : t('common.workstation.sendCommandBtn', 'Send Command')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.workstation.resolveEscalation', 'Resolve Hardware Escalation')}</DialogTitle>
        <DialogContent>
          {selectedEscalation && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>{selectedEscalation.issue_type}</AlertTitle>
                {selectedEscalation.description}
              </Alert>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('common.workstation.resolutionNotes', 'Resolution Notes')}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleResolveEscalation} variant="contained" color="success" disabled={resolveEscalationMutation.isPending || !resolutionNotes.trim()}>
            {resolveEscalationMutation.isPending ? t('common.resolving', 'Resolving...') : t('common.markAsResolved', 'Mark as Resolved')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(WorkstationMonitoringPage);
